import { droppedFiles, pickWithHandles } from '@/lib/file-handles';
import { pickVideoFiles, VIDEO_ACCEPT } from '@/lib/video-files';

/**
 * A content page's drop zone: the page takes the video right where the visitor
 * is, then turns into the app with the video in hand, so nothing has to be
 * picked twice. The page stays plain HTML until then; this script is all it
 * loads up front, and the app's code is fetched only once a file arrives (or,
 * ahead of time, when the pointer reaches the drop zone).
 *
 * Markup (build/content-pages.ts writes it): `[data-intake]` on the zone, with
 * `data-app` (the app address the page's button opens), `data-active`,
 * `data-opening`, `data-not-video` and `data-failed` for the words it shows,
 * `[data-drop-title]` and `[data-drop-status]` inside, and `a[data-pick]` on
 * every button that should open the file picker instead of following its link.
 */

export type IntakeState = 'idle' | 'dragging' | 'opening' | 'error';

export interface IntakeOptions {
  /** Turns the page into the app with these files; rejects when the app couldn't be loaded. */
  readonly open: (files: File[]) => Promise<void>;
  /** Fetches the app's code ahead of time (called at most once). */
  readonly warm?: () => void;
}

const hasFiles = (event: DragEvent): boolean => event.dataTransfer?.types.includes('Files') ?? false;

/** A click that means "open the link somewhere else" (new tab, window, download) keeps its usual meaning. */
const opensElsewhere = (event: MouseEvent): boolean => event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

/** Wires the zone up; the returned function takes everything it added away again. */
export function setUpIntake(zone: HTMLElement, { open, warm }: IntakeOptions): () => void {
  const doc = zone.ownerDocument;
  const listening = new AbortController();
  const { signal } = listening;
  const root = doc.documentElement;
  const words = zone.dataset;
  const title = zone.querySelector<HTMLElement>('[data-drop-title]');
  const status = zone.querySelector<HTMLElement>('[data-drop-status]');
  const idleTitle = title?.textContent ?? '';
  const picks = [...doc.querySelectorAll<HTMLAnchorElement>('a[data-pick]')];

  const input = doc.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = VIDEO_ACCEPT;
  input.hidden = true;
  input.tabIndex = -1;
  zone.append(input);

  // Dragging a file anywhere over the page says where it will go, even with the zone scrolled away.
  const veil = doc.createElement('div');
  veil.className = 'drop-veil';
  veil.setAttribute('aria-hidden', 'true');
  const veilLabel = doc.createElement('p');
  veilLabel.textContent = words.active ?? '';
  veil.append(veilLabel);
  doc.body.append(veil);

  let state: IntakeState = 'idle';
  const show = (next: IntakeState, message = '') => {
    state = next;
    zone.dataset.state = next;
    if (title) title.textContent = next === 'opening' ? (words.opening ?? idleTitle) : next === 'dragging' ? (words.active ?? idleTitle) : idleTitle;
    if (status) {
      status.textContent = message;
      status.hidden = message === '';
    }
    for (const pick of picks) pick.toggleAttribute('aria-disabled', next === 'opening');
  };

  let warmed = false;
  const warmUp = () => {
    if (warmed || !warm) return;
    warmed = true;
    warm();
  };

  const receive = async (list: readonly File[]) => {
    if (state === 'opening') return;
    const files = pickVideoFiles(list);
    // What happens next is said in the zone, which a pick from the closing button may have left far above.
    zone.scrollIntoView?.({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    if (files.length === 0) return show('error', words.notVideo);
    show('opening');
    try {
      await open(files);
    } catch {
      show('error', words.failed);
    }
  };

  for (const pick of picks) {
    pick.addEventListener(
      'click',
      (event) => {
        if (opensElsewhere(event)) return;
        event.preventDefault();
        if (state === 'opening') return;
        warmUp();
        // The system dialog where there is one, so a project made from this video can find it again by itself.
        void pickWithHandles(VIDEO_ACCEPT).then((files) => {
          if (files === null) input.click();
          else if (files.length > 0) void receive(files);
        });
      },
      { signal },
    );
  }
  input.addEventListener(
    'change',
    () => {
      const files = [...(input.files ?? [])];
      input.value = '';
      if (files.length > 0) void receive(files);
    },
    { signal },
  );

  zone.addEventListener('pointerenter', warmUp, { signal });
  zone.addEventListener('focusin', warmUp, { signal });

  let depth = 0;
  const endDrag = () => {
    depth = 0;
    delete root.dataset.dragging;
    if (state === 'dragging') show('idle');
  };
  doc.addEventListener(
    'dragenter',
    (event) => {
      if (!hasFiles(event) || state === 'opening') return;
      event.preventDefault();
      depth++;
      root.dataset.dragging = '';
      if (state !== 'dragging') show('dragging');
      warmUp();
    },
    { signal },
  );
  doc.addEventListener(
    'dragover',
    (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = state === 'opening' ? 'none' : 'copy';
    },
    { signal },
  );
  doc.addEventListener(
    'dragleave',
    (event) => {
      if (!hasFiles(event)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) endDrag();
    },
    { signal },
  );
  doc.addEventListener(
    'drop',
    (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      endDrag();
      if (event.dataTransfer) void receive(droppedFiles(event.dataTransfer));
    },
    { signal },
  );

  return () => {
    listening.abort();
    input.remove();
    veil.remove();
    delete root.dataset.dragging;
  };
}

/** What the app's entry offers a page that turns into it (src/boot.tsx). */
export interface AppEntry {
  startApp(root: HTMLElement, options: { readonly files: readonly File[] }): void;
}

/**
 * Turns the page into the app at `appUrl`, with the files handed over. The
 * address changes first, since the app reads it as it starts (language, what
 * the link asks for); Back returns to the page as it was.
 */
export async function takeOver(files: readonly File[], appUrl: string, load: () => Promise<AppEntry>): Promise<void> {
  history.pushState(null, '', appUrl);
  let app: AppEntry;
  try {
    app = await load();
  } catch (error) {
    history.back();
    throw error;
  }
  // The page's own styles would fight the app's; the app's are in place once `load` resolves.
  for (const style of document.querySelectorAll('style[data-page]')) style.remove();
  delete document.documentElement.dataset.dragging;
  const root = document.createElement('div');
  root.id = 'root';
  document.body.replaceChildren(root);
  window.scrollTo(0, 0);
  // Back (or Forward) to a page that isn't the app loads it for real.
  window.addEventListener('popstate', () => location.reload());
  app.startApp(root, { files });
}

/** Asks the browser to fetch the app's files ahead of time without running them (scripts and stylesheets). */
export function preload(urls: readonly string[], doc: Document = document): void {
  for (const url of urls) {
    const link = doc.createElement('link');
    if (url.endsWith('.css')) {
      link.rel = 'preload';
      link.setAttribute('as', 'style');
    } else link.rel = 'modulepreload';
    link.href = url;
    doc.head.append(link);
  }
}
